<template>
  <section :class="$style.card">
    <div :class="$style.title">{{ campaignId ? "Main information" : "New campaign" }}</div>

    <div :class="$style.formRow">
      <div :class="$style.formInput">
        <label for="campaignName" :class="$style.label">
          Campaign name
          <span :class="$style.pError">*</span>
        </label>
        <InputText
          id="campaignName"
          v-model="campaignName"
          :class="{ [$style.pInvalid]: campaignName && isDuplicate }"
          placeholder="Name"
          @input="validate"
        />
        <small v-if="isDuplicate" :class="[$style.info, $style.pError]">Campaign name already exists</small>
        <small v-if="campaignName.length > 100" :class="[$style.info, $style.pError]">
          Campaign name should not exceed 100 characters
        </small>
        <small :class="[$style.info, !isCampaignNameValid && $style.pError]">
          Use only alphanumeric characters, dollar signs and spaces
        </small>
      </div>

      <div :class="$style.formRow" style="width: 50%">
        <div :class="$style.formInput" style="width: 100%">
          <label for="purchaseLimit" :class="$style.label">
            Purchase limit
            <span :class="$style.pError">*</span>
          </label>
          <InputNumber
            id="purchaseLimit"
            v-model="purchaseLimit"
            :class="$style.purchaseLimit"
            :disabled="isInfinite"
            :min="1"
            :max="999"
          />
        </div>

        <div :class="[$style.formInput, $style.infinite]" style="align-items: center; margin-top: 1.8rem">
          <Checkbox v-model="isInfinite" :class="$style.checkbox" binary input-id="isInfinite" name="isInfinite" />
          <label for="isInfinite">Infinite</label>
        </div>
      </div>
    </div>

    <div v-if="flags.campaignPurchaseLimit.enabled && !campaignId" :class="$style.formRow">
      <div :class="$style.formInput" style="width: calc(50% - 0.5rem)">
        <label for="offers" :class="$style.label">Add offers to campaign</label>
        <MultiSelect
          id="offers"
          v-model="selectedOffers"
          :options="unassignedOffers"
          option-label="name"
          placeholder="Offers without a campaign"
        />
      </div>
    </div>

    <div :class="$style.actions" :style="{ marginTop: campaignId ? '-2rem' : '0' }">
      <div>
        <Button
          v-if="!campaignId"
          label="Cancel"
          class="p-button-primary"
          icon="pi pi-chevron-left"
          outlined
          style="margin-right: 0.5rem"
          @click="redirectToCampaignList"
        />
        <Button
          :label="campaignId ? 'Save changes' : 'Save and close'"
          class="p-button-primary"
          :disabled="!campaignName || isDuplicate || !isCampaignNameValid"
          @click="save"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import useFeatureFlags from "@/hooks/useFeatureFlags";
import useToast from "@/hooks/useToastNotification";
import useCampaigns from "@/modules/Offers/hooks/useCampaigns";
import { useOffers } from "@/modules/Offers/hooks/useOffers";
import links from "@/router/links";
import isAlphaNumeric from "@/utils/isAlphaNumeric";
import { Campaign, OfferItem } from "../types/types";

const router = useRouter();

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },

  campaignId: {
    type: String,
    required: false,
  },
});

const route = useRoute();
const { toastSuccess, toastError } = useToast();

const gameId = ref(route.params.gameId as string);
const versionId = ref(route.params.versionId as string);

const { flags } = useFeatureFlags(gameId.value);
const { campaigns, offersToDisplay, updateOffer } = useOffers(gameId.value, versionId.value);
const { updateCampaign, createCampaign } = useCampaigns(gameId.value, versionId.value);

const displayedCampaign = computed(() => campaigns.value?.find((campaign) => campaign.id === props.campaignId));

const unassignedOffers = computed(() =>
  offersToDisplay.value.filter((offer) => offer.campaignId === null && offer.status === "DRAFT")
);

const campaignName = ref("");
const isCampaignNameValid = ref(true);
const isDuplicate = ref(false);
const selectedOffers = ref<OfferItem[]>([]);
const purchaseLimit = ref(0);
const isInfinite = ref(false);

const validate = () => {
  campaignName.value = campaignName.value.trim();
  isCampaignNameValid.value = campaignName.value.length > 0 && campaignName.value.length <= 100;
  isCampaignNameValid.value = isAlphaNumeric({ str: campaignName.value, allowWhitespace: true, dollarSigns: true });

  isDuplicate.value = campaigns.value.some(
    (campaign: Campaign) => campaign.name === campaignName.value && campaign.id !== props.campaignId
  );
};

const handleOfferCampaignUpdate = async (offerId: string, campaignId: string) => {
  await updateOffer(offerId, { campaignId });
};

// Update offers sequentially in order to work with graphQL
const updateOffersSequentially = async (offers: OfferItem[], campaignId: string, index: number = 0) => {
  if (index < offers.length) {
    try {
      await handleOfferCampaignUpdate(offers[index].id, campaignId);
      await updateOffersSequentially(offers, campaignId, index + 1);
    } catch (error) {
      console.error("Error updating offer campaign:", error);
    }
  }
};

const redirectToCampaignList = () => {
  router.push({ name: links.offersAndStore.tabs.offerCampaigns.campaignList });
};

const save = async () => {
  validate();

  if (isCampaignNameValid.value && !isDuplicate.value) {
    const campaignData = {
      name: campaignName.value,
      purchaseLimit: purchaseLimit.value,
      id: props.campaignId || undefined,
    };

    try {
      if (props.campaignId) {
        await updateCampaign(campaignData);
        await updateOffersSequentially(selectedOffers.value, props.campaignId);
      } else {
        const createdCampaign = await createCampaign(campaignData);
        if (createdCampaign?.id) {
          await updateOffersSequentially(selectedOffers.value, createdCampaign.id);
        }
      }
    } catch (error) {
      toastError({
        detail: `An error occurred while saving the campaign ${campaignName.value}.`,
        summary: "Error",
        life: 3 * 1000,
      });
    } finally {
      if (!props.campaignId) {
        redirectToCampaignList();
      } else {
        toastSuccess({
          detail: `The campaign ${campaignName.value} has been updated successfully!`,
          summary: "Success",
          life: 3 * 1000,
        });
      }
    }
  }
};

watch(isInfinite, (newValue) => {
  purchaseLimit.value = newValue ? 0 : 1;
});

watch(displayedCampaign, () => {
  if (displayedCampaign.value) {
    campaignName.value = displayedCampaign.value.name;
    purchaseLimit.value = displayedCampaign.value.purchaseLimit || 0;
    isInfinite.value = purchaseLimit.value === 0;
  }
});
</script>

<style module lang="scss">
.formRow {
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
}

.formInput {
  display: flex;
  flex-direction: column;
  width: 50%;

  .purchaseLimit {
    flex: 1;
  }
}

.label {
  margin-bottom: 0.5rem;
  font-size: 14px;
}

.title {
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

.infinite {
  display: flex;
  flex: 1 1 0%;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
}

.card {
  background: white;
  border: 1px solid var(--vd-color-text-light-7);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.pInvalid {
  border-color: var(--vd-color-error);
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.pError {
  color: var(--vd-color-error);
  height: 8px;
}

.pValid {
  color: var(--vd-color-text-light-2);
}

.info {
  margin-top: 0.25rem;
}

.required {
  margin: 1.5rem 0;
  font-size: 12px;
}

.checkbox {
  margin-left: 0.5rem;
}
</style>
